import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, LineChart, Blocks, Code2, Workflow, Compass } from 'lucide-react';
import HeroBanner from '../components/HeroBanner';
import { homeContent, whatWeDoContent } from '../mock';

const iconMap = { LineChart, Blocks, Code2, Workflow, Compass };

const Home = () => {
  return (
    <>
      <HeroBanner
        image={homeContent.heroImage}
        quote={homeContent.heroQuote}
        attribution={homeContent.heroAttribution}
      />

      {/* Intro / mission */}
      <section className="bg-[#e5dfe9] py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-center text-[12px] tracking-[0.28em] font-sans font-semibold text-neutral-600 mb-6">
            {homeContent.intro.eyebrow}
          </p>
          <h1
            className="font-serif italic text-center text-neutral-800 mb-10 leading-tight"
            style={{ fontSize: 'clamp(2rem, 4.2vw, 3.25rem)' }}
          >
            {homeContent.intro.heading}
          </h1>
          <p
            className="max-w-3xl mx-auto text-center text-neutral-700 font-sans"
            style={{ fontSize: '18px', lineHeight: '1.8' }}
          >
            {homeContent.intro.lead}
          </p>

          <div className="flex justify-center mt-12">
            <Link
              to="/what-we-do"
              className="group inline-flex items-center gap-2 text-[13px] tracking-[0.22em] font-sans font-medium border border-neutral-700 text-neutral-800 rounded-full px-7 py-3 hover:bg-neutral-800 hover:text-white transition-colors"
            >
              EXPLORE OUR PRACTICES
              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
          </div>
        </div>
      </section>

      {/* Pillars preview grid */}
      <section className="bg-[#efeaf2] py-24 md:py-32 border-t border-neutral-300/40">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[12px] tracking-[0.28em] font-sans font-semibold text-neutral-600 mb-4">
              {homeContent.pillarsPreview.eyebrow}
            </p>
            <h2
              className="font-serif italic text-neutral-800 mb-6"
              style={{ fontSize: 'clamp(1.8rem, 3.6vw, 2.6rem)' }}
            >
              {homeContent.pillarsPreview.heading}
            </h2>
            <p
              className="max-w-2xl mx-auto text-neutral-700 font-sans"
              style={{ fontSize: '17px', lineHeight: '1.75' }}
            >
              {homeContent.pillarsPreview.description}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {whatWeDoContent.pillars.map((p) => {
              const Icon = iconMap[p.icon] || Compass;
              return (
                <Link
                  key={p.key}
                  to="/what-we-do"
                  className="group flex flex-col bg-white/60 backdrop-blur-sm border border-neutral-400/25 rounded-sm p-8 hover:bg-white hover:border-neutral-500/50 hover:shadow-md transition-all duration-300"
                >
                  <div className="w-11 h-11 rounded-full border border-neutral-500/40 flex items-center justify-center mb-6 group-hover:border-neutral-800 transition-colors">
                    <Icon size={20} className="text-neutral-700 group-hover:text-neutral-900 transition-colors" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-sans font-semibold text-neutral-800 mb-3" style={{ fontSize: '17px' }}>
                    {p.title}
                  </h3>
                  <p
                    className="text-neutral-600 font-sans flex-1"
                    style={{ fontSize: '14.5px', lineHeight: '1.65' }}
                  >
                    {p.body.split('. ')[0].replace(/\.$/, '') + '.'}
                  </p>
                  <span className="inline-flex items-center gap-1 mt-6 text-[12px] tracking-[0.22em] font-sans font-medium text-neutral-700 group-hover:text-neutral-900">
                    LEARN MORE
                    <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Approach section */}
      <section className="bg-[#e5dfe9] py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-20">
            <p className="text-[12px] tracking-[0.28em] font-sans font-semibold text-neutral-600 mb-4">
              {homeContent.approach.eyebrow}
            </p>
            <h2
              className="font-serif italic text-neutral-800 mb-6"
              style={{ fontSize: 'clamp(1.9rem, 3.6vw, 2.6rem)' }}
            >
              {homeContent.approach.heading}
            </h2>
            <p
              className="max-w-2xl mx-auto text-neutral-700 font-sans"
              style={{ fontSize: '17px', lineHeight: '1.75' }}
            >
              {homeContent.approach.description}
            </p>
          </div>

          <ol className="relative border-l border-neutral-500/30 ml-3 md:ml-6 space-y-14">
            {homeContent.approach.steps.map((s) => (
              <li key={s.n} className="pl-8 md:pl-12 relative">
                <span className="absolute -left-[13px] top-1 w-6 h-6 rounded-full bg-[#e5dfe9] border border-neutral-600 flex items-center justify-center">
                  <span className="w-1.5 h-1.5 rounded-full bg-neutral-700" />
                </span>
                <div className="flex items-baseline gap-4 mb-3 flex-wrap">
                  <span className="font-serif italic text-neutral-500" style={{ fontSize: '1.4rem' }}>
                    {s.n}
                  </span>
                  <h3 className="font-sans font-semibold text-neutral-800" style={{ fontSize: '20px' }}>
                    {s.title}
                  </h3>
                </div>
                <p
                  className="text-neutral-700 font-sans max-w-2xl"
                  style={{ fontSize: '16.5px', lineHeight: '1.75' }}
                >
                  {s.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Path to Participation impact section */}
      <section className="bg-neutral-900 text-white py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-[12px] tracking-[0.28em] font-sans font-semibold text-white/60 mb-4">
            {homeContent.impact.eyebrow}
          </p>
          <h2
            className="font-serif italic mb-8"
            style={{ fontSize: 'clamp(2rem, 4vw, 2.9rem)' }}
          >
            {homeContent.impact.heading}
          </h2>
          <p
            className="max-w-3xl mx-auto text-white/80 font-sans mb-16"
            style={{ fontSize: '17.5px', lineHeight: '1.8' }}
          >
            {homeContent.impact.body}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6">
            {homeContent.impact.stats.map((s, i) => (
              <div key={i} className="border-t border-white/20 pt-6">
                <p className="font-serif italic mb-2" style={{ fontSize: 'clamp(1.8rem, 3vw, 2.2rem)' }}>
                  {s.value}
                </p>
                <p className="text-white/70 font-sans text-[13px] tracking-[0.14em] uppercase">
                  {s.label}
                </p>
              </div>
            ))}
          </div>

          <div className="flex justify-center mt-16">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 text-[13px] tracking-[0.22em] font-sans font-medium border border-white/80 text-white rounded-full px-7 py-3 hover:bg-white hover:text-neutral-900 transition-colors"
            >
              START A CONVERSATION
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
